"use client";

import React from "react";
import { MapPin, FileText, Stethoscope, Rocket, Clock, CalendarCheck, ShieldCheck, Coffee } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "The Concierge Inquiry",
  description: "Begin your journey with a private interview and consultation. We explore your kennel's breed, brand, story, pedigrees, and your vision for a digital legacy that reflects your standards.",
  icon: MapPin
}, {
  title: "Strategic Curation",
  description: "Our artisans develop a Custom Website strategy. From branding, photos, pedigree integration to social media aesthetics, every detail is meticulously planned for the look and feel you want.",
  icon: FileText
}, {
  title: "The Launch",
  description: "Experience the unveiling of your website and digital asset. We provide a white-glove transition, ensuring your new website is as exactly as the program you are... a Best in Show winner.",
  icon: Rocket
}];
const faqs = [{
  question: "How long does the Custom Website design process take?",
  answer: "Typically, a full website takes 6 to 10 weeks. This ensures every pixel meets our 'Best in Show' standard and your pedigree is perfectly presented without compromise."
}, {
  question: "What assets do I need to provide?",
  answer: "We require high-resolution photography of your sires and dams, pedigrees, and a brief history of your breeding program's philosophy and heritage."
}, {
  question: "Do you handle ongoing social media management?",
  answer: "Yes, our Social Concierge service provides content scheduling and just-in-time posting of litters and or achievements, allowing you to focus on your dogs while we manage your digital presence."
}, {
  question: "Is the application process selective?",
  answer: "To maintain our high standards of service, we only accept a limited number of clients per quarter. We look for breeders dedicated to excellence and health preservation."
}];
export default function YourFirstVisit() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="28" id="contact" ref={ref} className={cn("py-24 bg-background relative overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[10%] -right-[5%] w-[40rem] h-[40rem] bg-secondary/20 rounded-full blur-3xl opacity-40" />
        <div className="absolute top-[40%] -left-[10%] w-[30rem] h-[30rem] bg-primary/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-6 tracking-tight">Your Journey to Best in Show (Website)</h2>
          <p className="text-lg font-sans text-muted-foreground leading-relaxed tracking-wide">
            We have refined our onboarding process to be as seamless and exclusive as the dogs you breed. 
            Your legacy deserves a digital home of unparalleled distinction.
          </p>
        </div>

        {}
        <div className="relative mb-24">
          {}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-border -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {steps.map((step, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center group">
                {}
                <div className="relative mb-6">
                  <div className="w-24 h-24 rounded-full bg-background border border-secondary/30 flex items-center justify-center shadow-lg group-hover:border-secondary group-hover:shadow-secondary/20 transition-all duration-500">
                    <step.icon className="w-10 h-10 text-secondary" strokeWidth={1} />
                  </div>
                  {}
                  <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-serif font-bold shadow-sm border border-background">
                    {i + 1}
                  </div>
                </div>

                <h3 className="text-xl font-serif font-semibold text-foreground mb-3 tracking-wide">
                  {step.title}
                </h3>
                <p className="text-muted-foreground font-sans text-sm leading-relaxed max-w-xs mx-auto tracking-normal">
                  {step.description}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <h3 className="text-2xl font-serif font-bold text-foreground mb-2">Concierge FAQ</h3>
              <p className="text-muted-foreground font-sans tracking-wide">Details for the discerning breeder.</p>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/60">
                  <AccordionTrigger className="text-left text-base font-serif font-medium text-foreground hover:text-secondary hover:no-underline py-5 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-sans text-base leading-relaxed pb-6 tracking-normal">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="lg:col-span-5">
            <div className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40 shadow-xl sticky top-24">
              <div className="flex items-center gap-3 mb-4 text-secondary">
                <CalendarCheck className="w-6 h-6" />
                <span className="font-sans font-semibold tracking-widest uppercase text-xs">Private Inquiry</span>
              </div>
              
              <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                Experience the PupzStudio Distinction
              </h3>
              
              <p className="text-muted-foreground font-sans mb-8 leading-relaxed text-sm tracking-wide">Our team is ready to welcome your kennel into our breed preservation circle. Apply today to secure your place in our next design cycle.</p>
              
              <div className="space-y-4">
                <Button asChild size="lg" className="w-full h-12 text-base font-sans font-medium shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 rounded-full transition-all">
                  <Link to="#">Book a Consultation </Link>
                </Button>
                
                <div className="flex items-center justify-center gap-2 text-xs font-sans text-muted-foreground pt-2 uppercase tracking-widest">
                  <Clock className="w-4 h-4" />
                  <span>Consultations by Appointment Only</span>
                </div>
              </div>

              {}
              <div className="mt-8 pt-6 border-t border-border/40 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-xs font-sans text-foreground/80 tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-secondary" />
                  <span>Legacy Protection</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-sans text-foreground/80 tracking-wider">
                  <Coffee className="w-4 h-4 text-secondary" />
                  <span>White-Glove Service</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}